/**
 * Inverse of `projectCompositionTranslations` (PLAT-9966).
 *
 * The renderer round-trips its current state via `getSource()` ~300ms
 * after we set a projected (translated) source on it. Without this step,
 * VideoSourceStore would adopt the projected text as canonical, leaking
 * translations into the save path and into the "switch back to Original"
 * code path which short-circuits on `isOriginalLang`.
 *
 * For each rewrite `(placementId, path, property, originalValue)` emitted
 * by the projection, walk the renderer-returned source, find the placement
 * by id, traverse the path, and restore `[property] = originalValue` on
 * the leaf element. Clones only the ancestor chains of mutated leaves —
 * siblings retain reference equality, matching the projection helper's
 * discipline.
 */
import { setLeafPropertyByPath } from './elementPathUtils';

import type { ProjectionRewrite } from './projectCompositionTranslations';

export function unprojectCompositionTranslations<
  T extends Record<string, unknown>,
>(source: T, rewrites: ReadonlyArray<ProjectionRewrite>): T {
  if (rewrites.length === 0) return source;
  const topLevel = (source as { elements?: unknown[] }).elements;
  if (!Array.isArray(topLevel) || topLevel.length === 0) return source;

  // Group rewrites by placementId so we apply all of them to one cloned
  // placement at a time — multiple controls on the same placement share
  // the per-placement clone.
  const byPlacement = new Map<string, ProjectionRewrite[]>();
  for (const r of rewrites) {
    const arr = byPlacement.get(r.placementId);
    if (arr) arr.push(r);
    else byPlacement.set(r.placementId, [r]);
  }

  let nextTop: unknown[] | null = null;
  for (let i = 0; i < topLevel.length; i++) {
    const node = topLevel[i];
    if (!node || typeof node !== 'object') continue;
    const id = (node as { id?: unknown }).id;
    if (typeof id !== 'string') continue;
    const placementRewrites = byPlacement.get(id);
    if (!placementRewrites) continue;

    let current = node as Record<string, unknown>;
    for (const rewrite of placementRewrites) {
      const next = setLeafPropertyByPath(
        current,
        rewrite.path,
        rewrite.property,
        rewrite.originalValue,
      );
      if (next) current = next;
    }
    if (current !== node) {
      if (!nextTop) nextTop = topLevel.slice();
      nextTop[i] = current;
    }
  }

  if (!nextTop) return source;
  return { ...source, elements: nextTop };
}
